import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import type { V2ReportRecord } from '../domain/report';

// Hands a generated PDF to the system: on iOS the file is written to the app's cache and offered
// through the system share sheet (Files, Mail, AirDrop, …); on the web it downloads. The record
// and bytes stay in the app either way (the report history keeps every generated file).
export type ShareCode = 'shared' | 'downloaded' | 'cancelled' | 'failed';

export interface ShareOutcome {
  ok: boolean;
  code: ShareCode;
  detail: string;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

export async function shareReportFile(record: V2ReportRecord, blob: Blob): Promise<ShareOutcome> {
  if (Capacitor.isNativePlatform()) {
    try {
      const data = await blobToBase64(blob);
      const written = await Filesystem.writeFile({ path: `reports/${record.fileName}`, data, directory: Directory.Cache, recursive: true });
      await Share.share({ title: record.fileName, files: [written.uri] });
      return { ok: true, code: 'shared', detail: '' };
    } catch (raw) {
      const text = raw instanceof Error ? raw.message : String(raw);
      if (/cancel/i.test(text)) return { ok: false, code: 'cancelled', detail: '' };
      return { ok: false, code: 'failed', detail: text };
    }
  }
  try {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = record.fileName;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return { ok: true, code: 'downloaded', detail: '' };
  } catch (raw) {
    return { ok: false, code: 'failed', detail: raw instanceof Error ? raw.message : String(raw) };
  }
}
